import { useEffect, useState } from 'react';
import { Navbar } from '@/components/Navbar';
import { Footer } from '@/components/Footer';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Link } from 'react-router-dom';
import { Users, Calendar, Heart, Award, ArrowRight, Sparkles } from 'lucide-react';
import { api, mediaUrl } from '@/admin/api';
import { useSiteSettings } from '@/contexts/SiteSettings';
import HeroCarousel from '@/components/HeroCarousel';

const HomePage = () => {
  const { settings } = useSiteSettings();
  const [content, setContent] = useState({
    hero_title: 'Strength, Unity, Heritage',
    hero_subtitle: '',
    hero_cta_label: 'Become a Member',
    hero_cta_link: '/membership',
    mission_preview: '',
  });
  const [upcomingEvents, setUpcomingEvents] = useState([]);
  const [newsItems, setNewsItems] = useState([]);

  useEffect(() => {
    api.get('/api/content/home').then((r) => setContent((c) => ({ ...c, ...r.data }))).catch(() => {});
    api.get('/api/events').then((r) => {
      const today = new Date().toISOString().slice(0, 10);
      const upcoming = r.data.filter((e) => e.date >= today).sort((a, b) => a.date.localeCompare(b.date)).slice(0, 3);
      setUpcomingEvents(upcoming);
    }).catch(() => {});
    api.get('/api/news').then((r) => setNewsItems(r.data.slice(0, 3))).catch(() => {});
  }, []);

  const stats = [
    { label: 'Active Members', value: '5,000+', icon: Users },
    { label: 'Local Chapters', value: '50+', icon: Award },
    { label: 'Annual Events', value: '30+', icon: Calendar },
    { label: 'Years of Service', value: '25+', icon: Heart },
  ];

  return (
    <div className="min-h-screen bg-background">
      <Navbar />

      {/* Hero Section */}
      <section className="relative overflow-hidden bg-gradient-to-br from-background via-muted/30 to-background">
        <div className="absolute inset-0 pattern-overlay opacity-30"></div>
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-20 lg:py-28 relative">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <div className="space-y-8">
              <div className="inline-flex items-center space-x-2 bg-accent/10 text-accent px-4 py-2 rounded-full">
                <Sparkles size={16} />
                <span className="text-sm font-medium">Empowering Tiv Women Since 1999</span>
              </div>
              <h1 className="text-4xl sm:text-5xl lg:text-6xl font-serif font-bold text-foreground leading-tight" data-testid="hero-title">
                {settings.site_name || 'Mzough U Kasev Tiv'}
              </h1>
              <p className="text-xl lg:text-2xl text-primary font-serif font-semibold">
                {content.hero_title}
              </p>
              <p className="text-lg text-muted-foreground leading-relaxed">
                {content.hero_subtitle || settings.tagline}
              </p>
              <div className="flex flex-col sm:flex-row gap-4">
                <Link to={content.hero_cta_link || '/membership'}>
                  <Button size="lg" className="bg-primary hover:bg-primary-dark text-white shadow-medium transition-elegant group" data-testid="hero-cta">
                    {content.hero_cta_label || 'Join Our Community'}
                    <ArrowRight className="ml-2 group-hover:translate-x-1 transition-transform" size={18} />
                  </Button>
                </Link>
                <Link to="/payment">
                  <Button size="lg" variant="outline" className="border-2 border-primary text-primary hover:bg-primary hover:text-white transition-elegant">
                    Pay for 2026 Summit
                  </Button>
                </Link>
              </div>
            </div>
            <div className="relative">
              <div className="absolute -inset-4 bg-gradient-to-r from-primary/20 to-accent/20 rounded-3xl blur-2xl"></div>
              <HeroCarousel className="relative" />
            </div>
          </div>
        </div>
      </section>

      {/* Stats Section */}
      <section className="py-16 bg-card">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-8">
            {stats.map((stat, index) => {
              const Icon = stat.icon;
              return (
                <div key={index} className="text-center space-y-3">
                  <div className="inline-flex items-center justify-center w-16 h-16 rounded-2xl bg-primary/10 text-primary">
                    <Icon size={32} />
                  </div>
                  <div>
                    <p className="text-3xl lg:text-4xl font-serif font-bold text-foreground">{stat.value}</p>
                    <p className="text-sm text-muted-foreground mt-1">{stat.label}</p>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* Mission Section */}
      <section className="py-20 bg-background">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center max-w-3xl mx-auto mb-16">
            <h2 className="text-3xl lg:text-4xl font-serif font-bold text-foreground mb-6">
              Our Mission
            </h2>
            <p className="text-lg text-muted-foreground leading-relaxed">
              {content.mission_preview || 'To unite Tiv women across Nigeria, preserve our rich cultural heritage, develop leadership potential, and create opportunities for social and economic empowerment.'}
            </p>
          </div>

          <div className="grid md:grid-cols-3 gap-8">
            <Card className="border-2 hover:border-primary transition-elegant hover:shadow-medium">
              <CardContent className="pt-8 pb-6 space-y-4 flex flex-col h-full">
                <div className="w-14 h-14 rounded-xl bg-primary/10 flex items-center justify-center">
                  <Users className="text-primary" size={28} />
                </div>
                <h3 className="text-xl font-serif font-semibold text-foreground">Unity & Sisterhood</h3>
                <p className="text-muted-foreground leading-relaxed flex-grow">
                  Building strong bonds among Tiv women through shared values, cultural pride, and mutual support.
                </p>
              </CardContent>
            </Card>

            <Card className="border-2 hover:border-accent transition-elegant hover:shadow-medium">
              <CardContent className="pt-8 pb-6 space-y-4 flex flex-col h-full">
                <div className="w-14 h-14 rounded-xl bg-accent/10 flex items-center justify-center">
                  <Award className="text-accent" size={28} />
                </div>
                <h3 className="text-xl font-serif font-semibold text-foreground">Leadership Development</h3>
                <p className="text-muted-foreground leading-relaxed flex-grow">
                  Empowering women with skills, knowledge, and platforms to lead in their communities and beyond.
                </p>
              </CardContent>
            </Card>

            <Card className="border-2 hover:border-secondary transition-elegant hover:shadow-medium">
              <CardContent className="pt-8 pb-6 space-y-4 flex flex-col h-full">
                <div className="w-14 h-14 rounded-xl bg-secondary/10 flex items-center justify-center">
                  <Heart className="text-secondary" size={28} />
                </div>
                <h3 className="text-xl font-serif font-semibold text-foreground">Cultural Preservation</h3>
                <p className="text-muted-foreground leading-relaxed flex-grow">
                  Celebrating and preserving Tiv traditions, language, and customs for future generations.
                </p>
              </CardContent>
            </Card>
          </div>
        </div>
      </section>

      {/* Upcoming Events */}
      <section className="py-20 bg-muted/30">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center mb-12">
            <div>
              <h2 className="text-3xl lg:text-4xl font-serif font-bold text-foreground mb-3">
                Upcoming Events
              </h2>
              <p className="text-muted-foreground">Join us in our upcoming activities and celebrations</p>
            </div>
            <Link to="/events" className="mt-4 sm:mt-0">
              <Button variant="outline" className="border-primary text-primary hover:bg-primary hover:text-white">
                View All Events
                <ArrowRight className="ml-2" size={16} />
              </Button>
            </Link>
          </div>

          <div className="grid md:grid-cols-3 gap-6">
            {upcomingEvents.length === 0 ? (
              <p className="text-muted-foreground col-span-3 text-center py-8">No upcoming events yet.</p>
            ) : upcomingEvents.map((event) => (
              <Card key={event.id} className="hover:shadow-medium transition-elegant overflow-hidden">
                {event.image_url && <img src={mediaUrl(event.image_url)} alt={event.title} className="w-full h-32 object-cover" />}
                <CardContent className="pt-6 space-y-4">
                  <Badge className="bg-primary/10 text-primary hover:bg-primary/20">{event.category || 'event'}</Badge>
                  <h3 className="text-xl font-serif font-semibold text-foreground">{event.title}</h3>
                  <div className="space-y-2 text-sm text-muted-foreground">
                    <div className="flex items-center space-x-2"><Calendar size={16} /><span>{event.date}</span></div>
                    {event.location && <div className="flex items-center space-x-2"><Users size={16} /><span>{event.location}</span></div>}
                  </div>
                  <Link to="/events"><Button variant="ghost" className="w-full text-primary hover:bg-primary/10 mt-4">Learn More</Button></Link>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* Latest News */}
      <section className="py-20 bg-background">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center mb-12">
            <div>
              <h2 className="text-3xl lg:text-4xl font-serif font-bold text-foreground mb-3">
                Latest News
              </h2>
              <p className="text-muted-foreground">Stay updated with our recent activities and announcements</p>
            </div>
            <Link to="/news" className="mt-4 sm:mt-0">
              <Button variant="outline" className="border-accent text-accent hover:bg-accent hover:text-white">
                All News
                <ArrowRight className="ml-2" size={16} />
              </Button>
            </Link>
          </div>

          <div className="grid md:grid-cols-3 gap-6">
            {newsItems.length === 0 ? (
              <p className="text-muted-foreground col-span-3 text-center py-8">No news posts yet.</p>
            ) : newsItems.map((item) => (
              <Card key={item.id} className="hover:shadow-medium transition-elegant overflow-hidden">
                {item.featured_image && <img src={mediaUrl(item.featured_image)} alt={item.title} className="w-full h-40 object-cover" />}
                <CardContent className="pt-6 space-y-4">
                  <p className="text-xs text-muted-foreground">{new Date(item.created_at).toLocaleDateString()}</p>
                  <h3 className="text-xl font-serif font-semibold text-foreground line-clamp-2">{item.title}</h3>
                  <p className="text-muted-foreground leading-relaxed line-clamp-3">{item.excerpt}</p>
                  <Link to="/news"><Button variant="link" className="text-primary p-0 h-auto font-medium">Read More<ArrowRight className="ml-1" size={14} /></Button></Link>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 bg-gradient-to-br from-primary to-accent text-white relative overflow-hidden">
        <div className="absolute inset-0 bg-[url('data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iNjAiIGhlaWdodD0iNjAiIHZpZXdCb3g9IjAgMCA2MCA2MCIgeG1sbnM9Imh0dHA6Ly93d3cudzMub3JnLzIwMDAvc3ZnIj48ZyBmaWxsPSJub25lIiBmaWxsLXJ1bGU9ImV2ZW5vZGQiPjxnIGZpbGw9IiNmZmZmZmYiIGZpbGwtb3BhY2l0eT0iMC4xIj48cGF0aCBkPSJNMzYgMzBoLTZWMGg2djMwem0wIDMwSDBoLTZ2LTZoMzZ2NnoiLz48L2c+PC9nPjwvc3ZnPg==')] opacity-20"></div>
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center relative">
          <h2 className="text-3xl lg:text-4xl font-serif font-bold mb-6">
            Ready to Join Our Community?
          </h2>
          <p className="text-lg lg:text-xl mb-8 text-white/90 leading-relaxed">
            Become a part of a powerful network of Tiv women dedicated to making a difference.
            Register today and start your journey with us.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Link to="/membership">
              <Button size="lg" variant="secondary" className="bg-white text-primary hover:bg-white/90 shadow-elevated">
                Register Now
              </Button>
            </Link>
            <Link to="/about">
              <Button size="lg" variant="outline" className="border-2 border-white text-white hover:bg-white hover:text-primary">
                Learn More About Us
              </Button>
            </Link>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default HomePage;
